import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import { isKidSite } from "../../../utils/isKidSite";
import {
  LoadingOverlay,
  Button,
  Paper,
  Text,
  Box,
  Stack,
  Group,
  ThemeIcon,
  Drawer,
  useMantineTheme,
} from "@mantine/core";
import {
  IconId,
  IconSunHigh,
  IconCapture,
  IconShieldX,
  IconArrowRight,
  IconCreditCard,
  IconChevronRight,
  IconCheck,
  IconRefresh,
} from "@tabler/icons-react";
import { isMobile as isMobileDevice, isFirefox, isTablet } from "react-device-detect";
import { DataContext } from "../../../context/DataContext";
import { processAndUploadWithIntegrity } from "../../../utils/captureIntegrity";
import FirefoxCameraWarning from "../../../components/FirefoxCameraWarning";
import DocumentCapture from "./capture";
import DocumentCaptureV2, { CAPTURE_BUILD_V2 } from "./capture-v2";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import useLocale from "../../../hooks/useLocale";
import { normalizeColor, tintOf, resolveCheckmarkColor } from "../../../helpers/component-styles";
import VerificationProcessingScreen from "../../../components/verification-processing-screen";
import useDeviceDetection from "../../../hooks/useDeviceDetection";
import { getDeviceSignalsUrl, getEdgeQueueUrl } from "../../../utils/edge";
import { preloadYoloModel, useYoloPreload } from "../../../utils/yoloModelCache";
import { resolveCaptureMode, captureOs } from "../../../hooks/useDocumentDetection";
import Handoff from "../../handoff";
import CompactHandoff from "../../../components/compact-handoff";
import BrowserHandoff from "../../../components/BrowserHandoff";
import AndroidCameraBlockPortal, {
  fireAndroidBlockRecoveredIfPending,
  fireAndroidBlockReturnedIfPending,
  getPendingAndroidBlock,
} from "../../../components/AndroidCameraBlockPortal";
import { getAndroidBlockVariant, shouldShowAndroidCameraBlock } from "../../../utils/androidDetection";
import VeratadFaceCapture from "../../face-capture/veratad";
import { trackEvent } from "../../../utils/telemetry";
import { flagEnabled, ID_DOC_CAPTURE_V2, machineReadEnabled } from "../../../utils/featureFlags";
import { resolveQualityOverrides } from "./qualityOverrides";
import useIsLandscape, { currentOrientation, useDeclareLandscapeSupport } from "../../../hooks/useOrientation";
import { useMediaQuery } from "@mantine/hooks";
import SetupStage from "./setup-stage";
import { suppliedImages, isDocumentSupplied, isSelfieSupplied } from "../../../utils/suppliedImages";

// Module-level callback for camera denial — set at module scope so it exists
// before any React component mounts. capture.js calls this via window global.
let _setIdDocCameraError = null;
window.__veratadIdDocCameraError = () => {
  console.log("[IdDocCapture] __veratadIdDocCameraError called, _setIdDocCameraError:", typeof _setIdDocCameraError);
  if (_setIdDocCameraError) _setIdDocCameraError(true);
};

// ── Front→Back transition screen ──

const FlipTransitionScreen = ({ locale }) => {
  const [step, setStep] = useState(0); // 0=front shown, 1=flipping, 2=back shown

  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 800);
    const t2 = setTimeout(() => setStep(2), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <Box
      style={{
        position: "fixed",
        top: 0,
        insetInlineStart: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        backgroundColor: "#111",
        gap: 24,
      }}
    >
      <style>{`
        @keyframes flipCard {
          0%   { transform: rotateY(0deg); }
          50%  { transform: rotateY(90deg); }
          100% { transform: rotateY(180deg); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Card flip animation */}
      <Box
        style={{
          perspective: 600,
          width: 160,
          height: 100,
        }}
      >
        <Box
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 12,
            border: "2px solid rgba(255,255,255,0.3)",
            backgroundColor: "rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: step >= 1 ? "flipCard 1.2s ease-in-out forwards" : "none",
            transformStyle: "preserve-3d",
          }}
        >
          {step < 2 ? (
            <IconCreditCard size={48} color="rgba(255,255,255,0.7)" stroke={1.2} />
          ) : (
            <Box style={{ transform: "rotateY(180deg)" }}>
              <IconCreditCard size={48} color="rgba(255,255,255,0.7)" stroke={1.2} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Status indicators */}
      <Group spacing="md" style={{ animation: "fadeSlideIn 0.5s ease-out" }}>
        <Box style={{ textAlign: "center", opacity: step >= 2 ? 0.5 : 1, transition: "opacity 0.4s" }}>
          <Text c="white" fz={13} fw={600}>
            {locale?.transition?.front || "FRONT"}
          </Text>
          <Text c="dimmed" fz={11}>✓ {locale?.transition?.captured || "Captured"}</Text>
        </Box>

        <IconArrowRight size={20} color="rgba(255,255,255,0.5)" className="rtl-mirror" />

        <Box style={{ textAlign: "center", opacity: step >= 2 ? 1 : 0.5, transition: "opacity 0.4s" }}>
          <Text c="white" fz={13} fw={600}>
            {locale?.transition?.back || "BACK"}
          </Text>
          <Text c="dimmed" fz={11}>{locale?.transition?.next || "Up next"}</Text>
        </Box>
      </Group>

      {/* Instruction */}
      <Text c="white" fz={16} fw={500} align="center" mt={8}
        style={{ animation: step >= 2 ? "fadeSlideIn 0.4s ease-out" : "none", opacity: step >= 2 ? 1 : 0 }}
      >
        {locale?.transition?.flipPrompt || "Flip your ID to the back side"}
      </Text>
    </Box>
  );
};

// ── Passport detection via MRZ + aspect ratio ──

async function analyzePassportFromBlob(blob) {
  try {
    const bitmap = await createImageBitmap(blob);
    const w = bitmap.width;
    const h = bitmap.height;

    // --- Aspect ratio check ---
    const aspectRatio = w / h;
    const aspectRatioPassport = aspectRatio >= 1.3 && aspectRatio <= 1.5;

    // --- MRZ detection via pixel analysis ---
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    // Scan bottom 30% for MRZ bands
    const mrzTop = Math.floor(h * 0.7);
    const mrzHeight = h - mrzTop;
    const imageData = ctx.getImageData(0, mrzTop, w, mrzHeight);
    const pixels = imageData.data;

    // Free the canvas immediately
    canvas.width = 0;
    canvas.height = 0;

    const darkThreshold = 80;
    const minDarkPercent = 0.35;
    let denseRowCount = 0;
    let bandCount = 0;
    let inBand = false;

    for (let row = 0; row < mrzHeight; row++) {
      let darkPixels = 0;
      for (let col = 0; col < w; col++) {
        const idx = (row * w + col) * 4;
        const luma = pixels[idx] * 0.299 + pixels[idx + 1] * 0.587 + pixels[idx + 2] * 0.114;
        if (luma < darkThreshold) darkPixels++;
      }
      if (darkPixels / w >= minDarkPercent) {
        if (!inBand) { bandCount++; inBand = true; }
        denseRowCount++;
      } else {
        inBand = false;
      }
    }

    const mrzDetected = bandCount >= 2 && denseRowCount >= 10;
    const isPassport = mrzDetected || aspectRatioPassport;

    console.log("[analyzePassport] result:", {
      isPassport, mrzDetected, aspectRatioPassport,
      aspectRatio: aspectRatio.toFixed(3), bandCount, denseRowCount,
    });

    return { isPassport };
  } catch (err) {
    console.warn("Passport analysis failed:", err);
    return { isPassport: false };
  }
}

// ── Main page component ──

export default function VeratadIdDocCapture({ forceStupidMode = false }) {
  const { data, settings, text, postPayloadMessage, dispatch } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("document-capture");
  const blockedLocale = useLocale("device-blocked");
  const allComponentStyles = data?.styles?.components;
  // Review check-marks: docCapture.reviewCheckColor, else the shared
  // checkmark.color, else today's light variant of the primary.
  const reviewCheckColor = resolveCheckmarkColor(
    allComponentStyles,
    allComponentStyles?.docCapture?.reviewCheckColor,
  );
  // "Before you get started" intro (shared by v1 and v2):
  //   docCapture.setupIconColor           guideline icons (well-lit, flat
  //                                       surface, four corners); falls back
  //                                       to checkmark.color, else text color
  //   docCapture.setupHeaderIconColor     ID icon above the title
  //   docCapture.setupHeaderIconBackground  disc behind that icon
  const setupIconColor = resolveCheckmarkColor(
    allComponentStyles,
    allComponentStyles?.docCapture?.setupIconColor,
  );
  const setupHeaderIconColor = normalizeColor(
    allComponentStyles?.docCapture?.setupHeaderIconColor,
  );
  const setupHeaderIconBackground = normalizeColor(
    allComponentStyles?.docCapture?.setupHeaderIconBackground,
  );
  // Document type picker (and the one-sided picker):
  //   docSelect.selectedBackgroundColor  fill of the highlighted option
  //   docSelect.borderColor              option borders (resting + hover)
  //   docSelect.iconColor                option icons
  const docSelectSelectedBg = normalizeColor(
    allComponentStyles?.docSelect?.selectedBackgroundColor,
  );
  const docSelectBorder = normalizeColor(allComponentStyles?.docSelect?.borderColor);
  const docSelectIcon = normalizeColor(allComponentStyles?.docSelect?.iconColor);
  // Optional explicit disc color behind the review checkmarks. Without
  // it the disc is a 15% tint of reviewCheckColor (or today's light
  // variant of the primary when neither key is set).
  const reviewCheckBackground = normalizeColor(
    data?.styles?.components?.docCapture?.reviewCheckBackground,
  );

  // ── Capture UI version (A/B) ──
  // v1 is the original manual capture; v2 is the refreshed screen
  // (capture-v2.js: dark chrome, landscape layout, light quality gates).
  // Resolved from the Flagship flag delivered in session settings, with a
  // ?force_capture_v2=true|false override for testing. Pinned in state at
  // first render so a late flag arrival can't swap the screen mid-flow;
  // by the time Get Started is tapped the session (and its flags) has
  // long since loaded, so a session that lands here after settings
  // resolve reads the real assignment.
  const [captureUi, setCaptureUi] = useState(() => resolveCaptureUi(settings));
  const isCaptureV2 = captureUi === "v2";
  // v2 lays out sideways, so the app-wide "rotate to portrait" prompt
  // stands down for the whole ID document step (intro, capture, review,
  // processing), not only while the capture screen itself is mounted.
  // The capture screen declares it too; the registry counts, so the
  // overlap is harmless. Remove once every SDK screen supports landscape.
  useDeclareLandscapeSupport(isCaptureV2);
  // v2 intro, held sideways: the capture's dark stage (brackets + a
  // schematic ID) sits beside the instructions instead of stacking
  // above them, so the screen that follows is previewed rather than
  // described. Portrait keeps the stacked intro.
  const viewportLandscape = useIsLandscape();
  const wideEnoughForSplit = useMediaQuery("(min-width: 640px)");
  const setupSideBySide = isCaptureV2 && viewportLandscape && wideEnoughForSplit;
  // Flags that land after mount are honored only while we're still on
  // the intro screen; once capture starts the arm is locked.
  useEffect(() => {
    if (phaseRef.current !== "setup") return;
    const next = resolveCaptureUi(settings);
    setCaptureUi((prev) => (prev === next ? prev : next));
    setMachineRead(machineReadEnabled(settings));
  }, [settings?.feature_flags]);

  // ── Machine-read shadow test (A/B) ──
  // On arm: after each photo the browser tries to read the PDF417
  // barcode and the MRZ from it and reports the outcome as one
  // id_doc_machine_read event (src/utils/machineRead). Nothing the user
  // sees or uploads changes. Pinned like the capture UI: a late flag is
  // honored on the intro screen only.
  const [machineRead, setMachineRead] = useState(() => machineReadEnabled(settings));
  const machineReadRef = useRef(machineRead);
  machineReadRef.current = machineRead;
  const machineReadModuleRef = useRef(null);
  // Longest the processing request waits for a read still running.
  const MACHINE_READ_WAIT_MS = 1000;
  const loadMachineRead = () => {
    if (!machineReadModuleRef.current) {
      machineReadModuleRef.current = import("../../../utils/machineRead");
      machineReadModuleRef.current.catch(() => {
        machineReadModuleRef.current = null;
      });
    }
    return machineReadModuleRef.current;
  };
  // Fire-and-forget: a failed load or read must never touch the flow.
  const shadowRead = (side, blob, oneSided = false) => {
    if (!machineReadRef.current || !blob) return;
    // A front that will be followed by a back gets one barcode attempt:
    // the license barcode is on the back, and the full set of attempts
    // would run while the user is shooting it (6.5 s on a test Android).
    // The MRZ is still read on every photo.
    const backExpected = !(oneSided || userDocType === "passport" || frontIdOnly || captureMode === "one_sided");
    // Where the session is, as far as the settings say: "aamva" (US or
    // Canada), "other", or "unknown" (treated like aamva).
    // ip_country is on the session data itself, not under settings.
    const cc = String(data?.ip_country || settings?.ip_country || settings?.country || "").trim().toUpperCase();
    const region = !/^[A-Z]{2,3}$/.test(cc) ? "unknown" : ["US", "USA", "CA", "CAN"].includes(cc) ? "aamva" : "other";
    try {
      loadMachineRead()
        .then((m) =>
          m.shadowReadDocument({
            blob,
            side,
            // What the user picked, when the flow asked: lets a passport skip the barcode attempts.
            hint: userDocType === "passport" ? "passport" : null,
            // One attempt also where no license barcode is expected: the
            // AAMVA barcode is a US / Canada format, and in the first
            // production hours (mostly other countries) 7 of 516 photos
            // had one while the attempts cost Android up to 2 s each.
            barcodeMode: (side === "front" && backExpected) || region === "other" ? "quick" : "full",
            region,
            telemetryBase: { ...telemetryBase, orientation: currentOrientation() },
          })
        )
        .catch(() => {});
    } catch {
      // never throw into the capture flow
    }
  };

  // ── Journey API prefill (utils/suppliedImages.js) ──
  // A supplied front means the document phase is already done: the user
  // goes straight to the live selfie, or, when the selfie was supplied
  // too (or the route has none), straight to processing with no camera.
  // Read once at mount: the step only renders after images_received_at
  // is set (routes.js holds it on SuppliedImagesPending until then).
  const [prefill] = useState(() => {
    const images = suppliedImages(settings);
    const docSupplied = isDocumentSupplied(settings);
    const selfieRequired = !!settings?.journey?.settings?.isSelfie;
    return {
      images,
      document: docSupplied,
      selfieOnly: docSupplied && selfieRequired && !isSelfieSupplied(settings),
      complete: docSupplied && (!selfieRequired || isSelfieSupplied(settings)),
    };
  });

  // Build capture settings early so isStupidMode is available for hooks
  const captureSettings = buildCaptureSettings(settings, captureUi);
  if (forceStupidMode) captureSettings.captureStrategy = isCaptureV2 ? "assisted" : "stupid";
  // Manual capture (no auto-capture): v1 "stupid", v2 "assisted" (adds
  // the light quality gates). Both route captures through the review step.
  const isStupidMode =
    captureSettings.captureStrategy === "stupid" || captureSettings.captureStrategy === "assisted";
  // Stamped onto every telemetry event this flow emits — keeps provider,
  // capture mode and the capture UI arm queryable across the whole doc
  // funnel without a join.
  const telemetryBase = {
    provider: "veratad",
    mode: resolveCaptureMode(captureSettings),
    capture_ui: captureUi,
    // v2 build stamp (see CAPTURE_BUILD_V2 in capture-v2.js).
    ...(captureUi === "v2" ? { capture_build: CAPTURE_BUILD_V2 } : {}),
    // Machine-read shadow test arm, on every event so the funnel splits
    // without a join. Locked once capture starts: read the arm from
    // id_doc_capture_started, not id_doc_landed.
    machine_read: machineRead,
    // Prefill sessions only, so other sessions' events are unchanged.
    ...(prefill.images.length > 0 ? { supplied_images: prefill.images.join(",") } : {}),
  };

  // Preload YOLO model as early as possible (skip in stupid mode)
  const { modelReady: _modelReady } = useYoloPreload(isStupidMode || prefill.document);
  const modelReady = isStupidMode ? true : _modelReady;

  useEffect(() => {
    // No models to preload in stupid mode, or when no document is shot here
    if (isStupidMode || prefill.document) return;
    preloadYoloModel().catch(() => {});
    // Inject <link rel="preload"> for the ONNX model file
    if (!document.querySelector('link[href="/models/document-detect/yolov8n-doc.onnx"]')) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.href = "/models/document-detect/yolov8n-doc.onnx";
      link.as = "fetch";
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    }
  }, []);

  // State machine: setup → captureFront → transition → captureBack → processing
  const [phase, setPhase] = useState("setup");
  const [loading, setLoading] = useState(false);
  const [isSDKComplete, setIsSDKComplete] = useState(false);

  // Selfie collection state. Prefill: a supplied document opens on the
  // selfie, and a supplied selfie counts as already collected.
  const [getFace, setGetFace] = useState(prefill.selfieOnly);
  const [gotFace, setGotFace] = useState(
    prefill.complete && !!settings?.journey?.settings?.isSelfie
  );

  // Capture state
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [detectedDocType, setDetectedDocType] = useState(null);
  const [userDocType, setUserDocType] = useState(null);
  const [docDrawerOpen, setDocDrawerOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  // v2 two-sided flow: both sides are reviewed together after the back
  // capture (ID Capture Redesign, page 3). A retake from that review
  // records which side is being replaced so the next capture returns
  // here instead of continuing the flow.
  const [combinedReview, setCombinedReview] = useState(false);
  const retakeSideRef = useRef(null);

  // Capture integrity — disabled until backend is deployed.
  // challengeRef stays null → processAndUploadWithIntegrity falls back to plain upload.
  const challengeRef = useRef(null);
  const sessionTimestampsRef = useRef({ session_start: Date.now() });

  // Mounted guard — prevents stale setPhase calls after unmount
  const mountedRef = useRef(true);
  const transitionTimerRef = useRef(null);
  const objectUrlsRef = useRef([]);
  // Tracks whether a capture has been initiated (front or back).
  // Used to prevent handleCaptureError from resetting to setup after
  // auto-capture triggers but before the phase transition completes
  // (toBlob is async so there's a small window).
  const captureInitiatedRef = useRef(false);

  // PostHog funnel event dedup refs
  const pageShownFiredRef = useRef(false);
  const captureStartedFiredRef = useRef(false);
  const captureCompletedFiredRef = useRef(false);
  const captureFailedFiredRef = useRef(false);
  const handoffCameraSuccessFiredRef = useRef(false);

  useEffect(() => {
    // Funnel top: the doc-capture step rendered — everything upstream
    // of the Get Started click (id_doc_capture_started) anchors to
    // this. `once` dedupes against the Dcams page firing the same
    // event when it hosts this component.
    trackEvent("id_doc_landed", { ...telemetryBase, orientation: currentOrientation() }, { once: true });
    return () => {
      mountedRef.current = false;
      if (machineReadModuleRef.current) {
        machineReadModuleRef.current.then((m) => m.disposeMachineRead()).catch(() => {});
      }
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
      // Revoke any outstanding object URLs
      objectUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
      });
    };
  }, []);

  // Device detection
  const { isTrulyMobile, serverTamperDetected, serverCheckPending } = useDeviceDetection({
    requestId: settings?.request_id,
    serverReportUrl: getDeviceSignalsUrl(),
  });
  const [onMobile, setOnMobile] = useState(isTrulyMobile);
  const [qrScanned, setQrScanned] = useState(false);

  // Prefill: no document camera here. The selfie screen shows its own
  // Firefox warning, and a fully supplied step uses no camera at all.
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobileDevice && isFirefox && !prefill.document
  );
  const [cameraError, setCameraError] = useState(false);

  // Browser handoff feature flag + force param
  // Identify on mount so all events share the same request_id identity.
  // Only evaluate the flag after a camera error to avoid triggering experiment exposure for all users.
  const [browserHandoffEnabled, setBrowserHandoffEnabled] = useState(false);
  const forceBrowserHandoff = new URLSearchParams(window.location.search).get("force_browser_handoff") === "true";
  const isBrowserHandoffSession = new URLSearchParams(window.location.search).get("browser_handoff") === "true";
  const flagCheckedRef = useRef(false);

  useEffect(() => {
    // If the user came back to this page after tapping "Refresh & Try Again"
    // on the Android camera block portal, fire the _returned event so the
    // funnel can distinguish "never came back" from "came back but still
    // blocked" drop-off. Fired inside the identify effect so the event is
    // guaranteed to be attributed to the request_id distinct_id, not an
    // anonymous one. Does not consume the sessionStorage marker.
    const pending = getPendingAndroidBlock();
    console.log("[Flow] Android block marker on mount:", pending ? "present" : "none");
    if (pending) {
      fireAndroidBlockReturnedIfPending({
        requestId: settings.request_id,
        captureType: "id_doc",
        isBrowserHandoffSession,
      });
    }
  }, []);

  // Auto-retry the camera if the Android block marker is present on page
  // load. The user already tapped refresh — they shouldn't have to also
  // re-tap "Get Started". If getUserMedia works now, recovery fires; if
  // it still fails, they see the block portal again (same as today).
  const androidAutoRetryRef = useRef(false);
  useEffect(() => {
    if (androidAutoRetryRef.current) return;
    // Prefill: the document camera never runs on this page.
    if (prefill.document) return;
    if (phase !== "setup") return;
    if (!settings?.request_id) return;
    if (showFirefoxPrompt) return;
    if (cameraError) return;
    if (!getPendingAndroidBlock()) return;
    androidAutoRetryRef.current = true;
    console.log("[Flow] Android block marker present — auto-retrying camera");
    handleStartCapture();
  }, [phase, settings?.request_id, showFirefoxPrompt, cameraError]);

  // Only check the flag after camera error. Flag now comes from Flagship via
  // settings.feature_flags (see utils/featureFlags); defaults to off until
  // an "id-doc-browser-handoff" flag is created in the fe-dcams Flagship app.
  useEffect(() => {
    if (!cameraError || flagCheckedRef.current) return;
    flagCheckedRef.current = true;
    if (flagEnabled("id-doc-browser-handoff")) {
      setBrowserHandoffEnabled(true);
    }
  }, [cameraError]);

  const shouldOfferBrowserHandoff = browserHandoffEnabled || forceBrowserHandoff;

  // Wire up the module-level callback on every render so it always has
  // fresh references to setState.
  _setIdDocCameraError = (val) => {
    console.log("[IdDocCapture] _setIdDocCameraError called, showing error");
    setLoading(false);
    setPhaseSync("setup");
    setCameraError(val);
  };

  // Listen for camera error DOM events from capture.js
  useEffect(() => {
    const handleCameraErrorEvent = () => {
      console.log("[IdDocCapture] veratad-camera-error DOM event received");
      setLoading(false);
      setCameraError(true);
      // Only reset to setup if capture hasn't been initiated
      if (!captureInitiatedRef.current) {
        phaseRef.current = "setup";
        setPhase("setup");
      }
    };
    window.addEventListener("veratad-camera-error", handleCameraErrorEvent);
    return () => window.removeEventListener("veratad-camera-error", handleCameraErrorEvent);
  }, []);

  // Should we skip the back based on settings?
  const frontIdOnly = settings?.journey?.settings?.frontIdOnly === true;

  // ── Route-level one-sided support (Japan MyNumber Card flow) ──
  const language = (
    data?.language ||
    settings?.language ||
    settings?.text?.language ||
    text?.language ||
    ""
  ).toLowerCase();

  const getCurrentRouteSettings = () => {
    const journey = settings?.journey;
    if (!journey?.route) return null;
    const currentRoute = journey.route.find(route =>
      route.path?.includes("mitek") || route.path?.includes("veratad-capture")
    );
    return currentRoute?.settings || null;
  };

  const routeSettings = getCurrentRouteSettings();
  const hasDocumentOptions = Array.isArray(routeSettings?.document_options) && routeSettings.document_options.length > 0;
  const offerOneSided = routeSettings?.offer_one_sided === true || hasDocumentOptions;

  const getLocalizedLabel = (labelObj, fallback) => {
    if (!labelObj) return fallback;
    return labelObj[language] || labelObj.en || fallback;
  };

  const oneSidedSectionTitle = getLocalizedLabel(
    routeSettings?.one_sided_section_title,
    language === "ja" ? "どの種類の身分証明書を使用しますか？" : "What type of ID would you like to use?"
  );
  const orLabel = getLocalizedLabel(
    routeSettings?.or_label,
    language === "ja" ? "または" : "or"
  );
  const oneSidedDocumentOptions = (() => {
    if (hasDocumentOptions) {
      return routeSettings.document_options.map((opt) => ({
        mode: opt.mode,
        label: getLocalizedLabel(opt.label, opt.mode === "one_sided" ? "Use single-sided ID" : "Use two-sided ID"),
        Icon: opt.icon === "passport" ? IconId : IconCreditCard,
      }));
    }
    return [
      {
        mode: "one_sided",
        label: getLocalizedLabel(routeSettings?.one_sided_button_label, "Use single-sided ID"),
        Icon: IconCreditCard,
      },
      {
        mode: "two_sided",
        label: getLocalizedLabel(routeSettings?.two_sided_button_label, "Use two-sided ID"),
        Icon: IconId,
      },
    ];
  })();

  const [captureMode, setCaptureMode] = useState(null);
  const [oneSidedDrawerOpen, setOneSidedDrawerOpen] = useState(false);

  // Update onMobile when device detection changes — but NOT during active capture.
  // Once capture starts, lock onMobile so device detection changes can't unmount the capture UI.
  const captureActive = phase !== "setup";
  useEffect(() => {
    if (captureActive) {
      console.log("[Flow] isTrulyMobile changed:", isTrulyMobile, "→ IGNORED (capture active, phase:", phase, ")");
      return;
    }
    console.log("[Flow] isTrulyMobile changed:", isTrulyMobile, "→ setOnMobile");
    setOnMobile(isTrulyMobile);
  }, [isTrulyMobile]);

  useEffect(() => {
    if (data?.isReactNative) {
      console.log("[Flow] isReactNative detected → setOnMobile(true)");
      setOnMobile(true);
    }
  }, [data?.isReactNative]);

  useEffect(() => {
    if (captureActive) return;
    if (settings?.journey?.settings?.showHandoffOnTablet && isTablet) {
      console.log("[Flow] showHandoffOnTablet + isTablet → setOnMobile(false)");
      setOnMobile(false);
    }
  }, [settings?.journey?.settings?.showHandoffOnTablet]);

  // Page shown (non-handoff only)
  const isHandoffMode = !onMobile && !(settings?.shouldShowContinueOnDesktop === true);
  useEffect(() => {
    if (isHandoffMode || pageShownFiredRef.current) return;
    pageShownFiredRef.current = true;
  }, []);

  // Terminal status captured from polling. VerificationProcessingScreen
  // holds "Completing verification…" until this is non-null, then
  // fires onComplete which dispatches navigation.
  const [terminalStatus, setTerminalStatus] = useState(null);

  const captureTerminal = useCallback((result) => {
    if (!result) return true;
    const status = result.status;
    const processState = result.process_state;
    const isTerminal =
      processState === "NEXT_APP_TRIGGER" ||
      processState === "COMPLETED" ||
      status === "PASS" ||
      status === "FAIL";
    if (isTerminal) {
      trackEvent("id_doc_verification_done", { ...telemetryBase, status, process_state: processState });
    }
    if (processState === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      return false;
    }
    if (processState === "COMPLETED") {
      setTerminalStatus(status === "PASS" ? "PASS" : "FAIL");
      return false;
    }
    if (status === "PASS") {
      setTerminalStatus("PASS");
      return false;
    }
    if (status === "FAIL") {
      setTerminalStatus("FAIL");
      return false;
    }
    return true;
  }, []);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    dispatch({ type: "SET_STATUS", payload: terminalStatus || "FAIL" });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch]);

  // For doc+selfie flows, hold off on polling until the selfie has
  // also been collected — otherwise the backend's NEXT_APP_TRIGGER on
  // the doc side fires the screen's onComplete and the journey
  // navigates away before we redirect to face capture.
  const needsSelfieNext =
    settings?.journey?.settings?.isSelfie && !gotFace;
  const shouldPoll = isSDKComplete && !needsSelfieNext;

  // Status polling (same pattern as dcams/index.js)
  if (data?.edge === true) {
    useEdgeStatusCheck(shouldPoll, captureTerminal);
  } else {
    useStatusCheck(shouldPoll, false, "id-doc/veratad-capture", captureTerminal);
  }

  // Prefill: the publisher's document stands in for the capture, so do
  // what uploadAllAndFinalize does once the photos are up. A fully
  // supplied step then submits at once (the effect below); a selfie-only
  // step submits when the face capture completes.
  useEffect(() => {
    if (!prefill.document) return;
    data.request_id = settings.request_id;
    data.country = settings.country;
    if (!("front_ext" in data)) {
      data.front_ext = "jpeg";
      data.back_ext = "jpeg";
    }
    trackEvent("id_doc_prefill_applied", {
      ...telemetryBase,
      next: prefill.complete ? "processing" : "selfie",
    });
    if (postPayloadMessage) {
      postPayloadMessage({
        event: "VERIFICATION_INITIATED",
        app: "id-doc/veratad-capture",
      });
    }
    if (prefill.complete) setIsSDKComplete(true);
  }, []);

  // When SDK is complete, check for selfie requirement before processing
  useEffect(() => {
    if (!isSDKComplete) return;

    // If selfie is required and we haven't collected it yet, redirect to face capture
    if (settings.journey?.settings?.isSelfie && !gotFace) {
      setIsSDKComplete(false);
      setGetFace(true);
      return;
    }

    setLoading(true);
    data.settings = settings;
    data.type = "SDK_SCAN";

    const edgeQueueUrl = getEdgeQueueUrl();

    const apiUrl =
      data?.edge === true
        ? edgeQueueUrl
        : "https://vx.dcams.app/frontend/dcams/process";

    const requestBody =
      data?.edge === true ? { ...data, queue: "dcams" } : data;

    // Include detected document type
    if (detectedDocType) {
      requestBody.detected_document_type = detectedDocType;
    }

    // Machine-read arm, edge sessions: attach what the browser decoded as
    // browser_extracted_data { barcode, mrz } (fixed field names, see
    // src/utils/machineRead). Waits at most MACHINE_READ_WAIT_MS for a
    // read still running; the telemetry flags say what was attached, never
    // the values.
    const extractedReady =
      machineReadRef.current && data?.edge === true && machineReadModuleRef.current
        ? machineReadModuleRef.current.then((m) => m.getExtractedData(MACHINE_READ_WAIT_MS)).catch(() => null)
        : Promise.resolve(null);
    extractedReady
      .then((extractedResult) => {
        if (extractedResult?.data) requestBody.browser_extracted_data = extractedResult.data;
        trackEvent("id_doc_processing_requested", {
          ...telemetryBase,
          ...(extractedResult
            ? {
                extracted_barcode: !!extractedResult.data?.barcode,
                extracted_mrz: !!extractedResult.data?.mrz,
                extracted_wait_ms: extractedResult.wait_ms,
                extracted_pending: extractedResult.pending,
              }
            : {}),
        });
        return fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });
      })
      .then((res) => {
        if (!res.ok) {
          trackEvent("id_doc_processing_failed", { ...telemetryBase, stage: "response", status: res.status });
        }
        return res.json();
      })
      .then(() => setLoading(false))
      .catch((err) => {
        // ~1.7% of processing requests never get a document verdict on
        // either arm; this separates a failed POST from a queue drop.
        console.error("Process request failed:", err);
        trackEvent("id_doc_processing_failed", { ...telemetryBase, stage: "request", error: err?.message || String(err) });
        setLoading(false);
      });
  }, [isSDKComplete, gotFace]);

  // ── Phase handlers ──

  // Track phase for error handler (avoids stale closures).
  // IMPORTANT: always update phaseRef synchronously before calling setPhase,
  // so error handlers see the correct phase immediately (not after React re-renders).
  const phaseRef = useRef(phase);
  function setPhaseSync(newPhase) {
    if (!mountedRef.current) {
      console.log("[Flow] Phase:", phaseRef.current, "→", newPhase, "(BLOCKED — unmounted)");
      return;
    }
    console.log("[Flow] Phase:", phaseRef.current, "→", newPhase);
    phaseRef.current = newPhase;
    setPhase(newPhase);
  }

  // Pre-acquire camera permission inside the user gesture context.
  // Chrome Android requires a recent user gesture to show the permission prompt.
  // After React re-renders and mounts <Webcam>, the gesture context is lost,
  // causing Chrome to auto-deny without showing a prompt.
  // We keep the stream alive and pass it to DocumentCapture, which renders a
  // plain <video> element instead of react-webcam (which always calls
  // getUserMedia internally and can't accept an existing stream).
  const preAcquiredStreamRef = useRef(null);

  async function handleStartCapture() {
    if (showFirefoxPrompt) return;
    console.log("[Flow] handleStartCapture → captureFront");
    if (!captureStartedFiredRef.current) {
      captureStartedFiredRef.current = true;
      trackEvent("id_doc_capture_started", { ...telemetryBase, orientation: currentOrientation() });
    }
    setLoading(true);
    sessionTimestampsRef.current.capture_start = Date.now();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 },
          // Android Chrome honours this; others ignore it (see capture-v2).
          advanced: [{ focusMode: "continuous" }],
        },
      });
      preAcquiredStreamRef.current = stream;

      // Machine-read arm: fetch the decoders now that the camera is ours.
      // They used to start alongside the permission request; in the first
      // production hours the on arm then showed more NotAllowedError
      // camera failures (7.4% of 878 sessions against 5.8% of 16,924).
      // No mechanism was found and it may be chance, but nothing about
      // the test should run before the permission prompt is answered.
      if (machineReadRef.current) {
        loadMachineRead().then((m) => m.preloadMachineRead()).catch(() => {});
      }

      // If the Android camera block was shown earlier in this tab, camera
      // is now working — fire the recovery event at the earliest definitive
      // signal (pre-acquire success) instead of waiting for models to load.
      // No-op if no sessionStorage marker is present.
      fireAndroidBlockRecoveredIfPending({
        requestId: settings.request_id,
        captureType: "id_doc",
        isBrowserHandoffSession,
      });

      setPhaseSync("captureFront");
    } catch (err) {
      console.error("[Flow] Pre-permission getUserMedia failed:", err?.name, err?.message);
      preAcquiredStreamRef.current = null;
      setLoading(false);

      setCameraError(true);
      // On Android, cameraError + shouldShowAndroidCameraBlock() in the
      // render branch routes the user to AndroidCameraBlockPortal. On
      // non-Android devices, the handoff flag decides what to show next.
      handleCaptureError({
        type: "camera_error",
        name: err?.name || "UnknownError",
        message: err?.message || "Camera access failed",
        constraint: err?.constraint || null,
      });
    }
  }

  function handleDocTypeSelect(docType) {
    setUserDocType(docType);
    setDocDrawerOpen(false);
    handleStartCapture();
  }

  function handleOneSidedSelection(mode) {
    setCaptureMode(mode);
    setOneSidedDrawerOpen(false);
    handleStartCapture();
  }

  // Stupid mode: intercept capture to show review drawer before proceeding
  function handleCaptureForReview(side, blob, previewUrl, docType, conf, frameChain) {
    if (isStupidMode && isCaptureV2) {
      if (side === "front") {
        // Front-only flows (passport picked, one-sided ID, front-only
        // sites) keep the single-photo review. Everything else goes
        // straight on to the back and is reviewed once, at the end.
        const willSkipBack = userDocType === "passport" || frontIdOnly || captureMode === "one_sided";
        if (willSkipBack) {
          setReviewData({ side, blob, previewUrl, docType, conf, frameChain });
          return;
        }
        if (retakeSideRef.current === "front") {
          retakeSideRef.current = null;
          replaceFrontCapture(blob, previewUrl, docType, frameChain);
          setCombinedReview(true);
          setPhaseSync("captureBack");
          return;
        }
        handleFrontCapture(blob, previewUrl, docType, conf, frameChain);
        return;
      }
      retakeSideRef.current = null;
      captureInitiatedRef.current = true;
      objectUrlsRef.current.push(previewUrl);
      setBackImage(previewUrl);
      backDataRef.current = { blob, docType, frameChain };
      shadowRead("back", blob);
      sessionTimestampsRef.current.capture_end = Date.now();
      trackEvent("id_doc_review_shown", { ...telemetryBase, review: "combined" });
      setCombinedReview(true);
      return;
    }
    if (isStupidMode) {
      setReviewData({ side, blob, previewUrl, docType, conf, frameChain });
      return;
    }
    if (side === "front") {
      handleFrontCapture(blob, previewUrl, docType, conf, frameChain);
    } else {
      handleBackCapture(blob, previewUrl, docType, conf, frameChain);
    }
  }

  function handleReviewAccept() {
    const { side, blob, previewUrl, docType, conf, frameChain } = reviewData;
    setReviewData(null);
    if (side === "front") {
      handleFrontCapture(blob, previewUrl, docType, conf, frameChain);
    } else {
      handleBackCapture(blob, previewUrl, docType, conf, frameChain);
    }
  }

  // A retaken front replaces the stored blob and re-uploads. The new
  // upload is chained behind the one already in flight (same S3 key), so
  // the retake is always the last write, whatever the network does.
  function replaceFrontCapture(blob, previewUrl, docType, frameChain) {
    captureInitiatedRef.current = true;
    objectUrlsRef.current.push(previewUrl);
    setFrontImage(previewUrl);
    setDetectedDocType(docType);
    sessionTimestampsRef.current.capture_end = Date.now();
    frontDataRef.current = { blob, docType, frameChain };
    shadowRead("front", blob);
    const previous = frontUploadRef.current;
    frontUploadRef.current = (previous ? previous.catch(() => {}) : Promise.resolve()).then(() => startFrontUpload());
    frontUploadRef.current.catch(() => {});
  }

  function handleCombinedRetake(side) {
    console.log("[Flow] Combined review: retake", side);
    trackEvent("id_doc_review_retake", { ...telemetryBase, review: "combined", side });
    retakeSideRef.current = side;
    setCombinedReview(false);
    captureInitiatedRef.current = false;
    setPhaseSync(side === "front" ? "captureFront" : "captureBack");
  }

  function handleCombinedAccept() {
    console.log("[Flow] Combined review accepted → processing");
    trackEvent("id_doc_review_accepted", { ...telemetryBase, review: "combined" });
    setCombinedReview(false);
    setPhaseSync("processing");
    uploadAllAndFinalize();
  }

  function handleReviewRetake() {
    if (reviewData?.previewUrl) {
      URL.revokeObjectURL(reviewData.previewUrl);
    }
    setReviewData(null);
    captureInitiatedRef.current = false;
  }

  // Close (X) on the v2 capture screen: abandon the in-progress capture
  // and return to the intro screen. The stream is stopped by the capture
  // screen's unmount cleanup; anything captured so far is discarded so a
  // restart begins cleanly at the front.
  function handleCancelCapture() {
    console.log("[Flow] handleCancelCapture, phase:", phaseRef.current);
    trackEvent("id_doc_capture_cancelled", {
      ...telemetryBase,
      phase: phaseRef.current,
      orientation: currentOrientation(),
    });
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    if (reviewData?.previewUrl) {
      URL.revokeObjectURL(reviewData.previewUrl);
    }
    setReviewData(null);
    setCombinedReview(false);
    if (machineReadModuleRef.current) {
      machineReadModuleRef.current.then((m) => m.clearExtractedData()).catch(() => {});
    }
    retakeSideRef.current = null;
    frontDataRef.current = null;
    backDataRef.current = null;
    frontUploadRef.current = null;
    captureInitiatedRef.current = false;
    preAcquiredStreamRef.current = null;
    setFrontImage(null);
    setBackImage(null);
    setLoading(false);
    setPhaseSync("setup");
  }

  // Store captured images + metadata for batch upload after all captures complete
  const frontDataRef = useRef(null);
  // The front upload, started early on v2 (see handleFrontCapture) or by
  // uploadAllAndFinalize. Cleared on cancel so a restart uploads afresh.
  const frontUploadRef = useRef(null);
  const backDataRef = useRef(null);

  // True when the current upload batch was initiated via the Android
  // camera-app fallback (user tapped "Take photo with camera app" on the
  // Android block portal and submitted a still from the OS camera app).
  // Used to fire the _fallback_uploaded event in uploadAllAndFinalize.
  const fallbackUploadPendingRef = useRef(false);

  // True after the user has submitted a file via the camera-app fallback.
  // Used to bypass the force_android_block early return so the loading
  // overlay and processing phase render normally — without this,
  // force mode would keep showing the portal even after the file was
  // uploaded, making users think nothing happened and tap again.
  const [fallbackInProgress, setFallbackInProgress] = useState(false);

  function handlePhotoFallback(file) {
    if (!file) return;
    console.log(
      "[Flow] handlePhotoFallback: file received",
      file.name,
      file.size,
      file.type
    );
    const previewUrl = URL.createObjectURL(file);
    fallbackUploadPendingRef.current = true;
    setFallbackInProgress(true);
    // Route through the existing front-only upload pipeline. No YOLO
    // doc type, no confidence, no frame chain — just the raw blob from
    // the camera app. forceOneSided = true bypasses captureMode state
    // so back capture is skipped even though we never set state.
    handleFrontCapture(file, previewUrl, null, null, null, true);
  }

  function handleFrontCapture(blob, previewUrl, docType, conf, frameChain, forceOneSided = false) {
    console.log("[Flow] handleFrontCapture called, docType:", docType, "conf:", conf, "forceOneSided:", forceOneSided);
    captureInitiatedRef.current = true;
    objectUrlsRef.current.push(previewUrl);
    setFrontImage(previewUrl);
    setDetectedDocType(docType);
    sessionTimestampsRef.current.capture_end = Date.now();

    // Store blob for later upload
    frontDataRef.current = { blob, docType, frameChain };
    shadowRead("front", blob, forceOneSided);
    // v2: start the front upload now, while the user turns the card over
    // and shoots the back; uploadAllAndFinalize awaits it instead of
    // starting it. Hides ~2s (4s at p90) of the end-of-capture wait.
    if (isCaptureV2 && !frontUploadRef.current) {
      frontUploadRef.current = startFrontUpload();
      // A failure is handled where the upload is awaited; this only keeps
      // an early rejection from surfacing as unhandled in the meantime.
      frontUploadRef.current.catch(() => {});
    }

    // Quick passport check using YOLO classification or user selection (synchronous)
    const isPassportByYolo = docType === "passport";
    const isPassportByUser = userDocType === "passport";
    const skipBack = isPassportByYolo || isPassportByUser || frontIdOnly || captureMode === "one_sided" || forceOneSided;

    if (skipBack) {
      console.log("[Flow] Skipping back (docType:", docType, "userDocType:", userDocType, "frontIdOnly:", frontIdOnly, "captureMode:", captureMode, ") → processing");
      setPhaseSync("processing");
      uploadAllAndFinalize();
      return;
    }

    // Not obviously a passport — transition to back immediately.
    // CRITICAL: setPhaseSync MUST be called synchronously so phaseRef
    // updates before any async callbacks (camera errors, re-renders).
    console.log("[Flow] Need back → transition");
    setPhaseSync("transition");
    transitionTimerRef.current = setTimeout(() => {
      console.log("[Flow] Transition done → captureBack");
      setPhaseSync("captureBack");
    }, isCaptureV2 ? 2000 : 4000);

    // Fire async MRZ analysis in background — if it detects a passport,
    // cancel the back capture and go straight to processing.
    // Skip for stupid mode (user already selected document type)
    if (isStupidMode) return;
    analyzePassportFromBlob(blob)
      .then((result) => {
        if (result.isPassport && mountedRef.current) {
          console.log("[Flow] Async passport analysis: IS passport — cancelling back capture");
          if (transitionTimerRef.current) {
            clearTimeout(transitionTimerRef.current);
            transitionTimerRef.current = null;
          }
          setPhaseSync("processing");
          uploadAllAndFinalize();
        } else {
          console.log("[Flow] Async passport analysis: not passport, continuing to back");
        }
      })
      .catch((err) => {
        console.warn("[Flow] Passport analysis failed, continuing to back:", err);
      });
  }

  function handleBackCapture(blob, previewUrl, docType, conf, frameChain) {
    console.log("[Flow] handleBackCapture called, docType:", docType);
    captureInitiatedRef.current = true;
    objectUrlsRef.current.push(previewUrl);
    setBackImage(previewUrl);
    sessionTimestampsRef.current.capture_end = Date.now();

    // Store blob for later upload
    backDataRef.current = { blob, docType, frameChain };
    shadowRead("back", blob);

    // All captures done — go to processing and upload
    console.log("[Flow] Back captured → processing");
    setPhaseSync("processing");
    uploadAllAndFinalize();
  }

  function startFrontUpload() {
    const { blob, frameChain } = frontDataRef.current;
    const imageKey = `${settings.request_id}_dl_front.jpeg`;
    console.log("[Flow] Queuing front upload:", imageKey);
    trackEvent("id_doc_upload_started", { ...telemetryBase, image: "front", bytes: blob?.size });
    const frontUploadStart = performance.now();
    return processAndUploadWithIntegrity(
      imageKey, blob, challengeRef.current, settings.request_id,
      "front", frameChain, { ...sessionTimestampsRef.current }, settings
    ).then((r) => {
      trackEvent("id_doc_upload_completed", { ...telemetryBase, image: "front", upload_ms: Math.round(performance.now() - frontUploadStart) });
      return r;
    });
  }

  async function uploadAllAndFinalize() {
    console.log("[Flow] uploadAllAndFinalize starting. front:", !!frontDataRef.current, "back:", !!backDataRef.current);
    try {
      const uploads = [];

      // Upload front (already in flight on v2)
      if (frontDataRef.current) {
        if (!frontUploadRef.current) frontUploadRef.current = startFrontUpload();
        uploads.push(frontUploadRef.current);
      }

      // Upload back (if captured)
      if (backDataRef.current) {
        const { blob, frameChain } = backDataRef.current;
        const imageKey = `${settings.request_id}_dl_back.jpeg`;
        console.log("[Flow] Queuing back upload:", imageKey);
        trackEvent("id_doc_upload_started", { ...telemetryBase, image: "back", bytes: blob?.size });
        const backUploadStart = performance.now();
        uploads.push(
          processAndUploadWithIntegrity(
            imageKey, blob, challengeRef.current, settings.request_id,
            "back", frameChain, sessionTimestampsRef.current, settings
          ).then((r) => {
            trackEvent("id_doc_upload_completed", { ...telemetryBase, image: "back", upload_ms: Math.round(performance.now() - backUploadStart) });
            return r;
          })
        );
      }

      console.log("[Flow] Uploading", uploads.length, "images...");
      await Promise.all(uploads);
      console.log("[Flow] All uploads complete");

      if (!captureCompletedFiredRef.current) {
        captureCompletedFiredRef.current = true;
        trackEvent("id_doc_capture_completed", { ...telemetryBase });
      }

      // If this upload was driven by the Android camera-app fallback,
      // fire the _fallback_uploaded event so the funnel can measure
      // end-to-end fallback success.
      if (fallbackUploadPendingRef.current) {
        fallbackUploadPendingRef.current = false;
        const front = frontDataRef.current;
        trackEvent("android_camera_os_block_fallback_uploaded", {
          capture_type: "id_doc",
          is_browser_handoff: isBrowserHandoffSession,
          file_size_bytes: front?.blob?.size,
          file_type: front?.blob?.type,
        });
      }

      data.request_id = settings.request_id;
      data.country = settings.country;
      if (!("front_ext" in data)) {
        data.front_ext = "jpeg";
        data.back_ext = "jpeg";
      }

      setIsSDKComplete(true);
      console.log("[Flow] SDK complete");

      if (postPayloadMessage) {
        postPayloadMessage({
          event: "VERIFICATION_INITIATED",
          app: "id-doc/veratad-capture",
        });
      }
    } catch (err) {
      console.error("[Flow] Upload failed:", err);
      trackEvent("id_doc_upload_failed", { ...telemetryBase, error: err?.message || String(err) });
      data.request_id = settings.request_id;
      data.country = settings.country;
      if (!("front_ext" in data)) {
        data.front_ext = "jpeg";
        data.back_ext = "jpeg";
      }
      setIsSDKComplete(true);
    }
  }

  function handleCaptureReady() {
    console.log("[Flow] handleCaptureReady — camera + models ready");
    // Track camera success specifically in browser handoff sessions
    if (isBrowserHandoffSession && !handoffCameraSuccessFiredRef.current) {
      handoffCameraSuccessFiredRef.current = true;
      trackEvent("id_doc_browser_handoff_camera_succeeded");
    }
    // If the Android camera block was shown earlier in this tab, the user
    // followed the instructions and camera is now working — fire the
    // recovery event for the funnel. No-op if no marker is present.
    fireAndroidBlockRecoveredIfPending({
      requestId: settings.request_id,
      captureType: "id_doc",
      isBrowserHandoffSession,
    });
    setLoading(false);
  }

  function handleCaptureError(err) {
    const currentPhase = phaseRef.current;
    console.error("[Flow] handleCaptureError, phase:", currentPhase, "captureInitiated:", captureInitiatedRef.current, "err:", err);

    if (!captureFailedFiredRef.current) {
      captureFailedFiredRef.current = true;
      trackEvent("id_doc_capture_failed", {
        ...telemetryBase,
        error_type: err?.type || "unknown",
        error_name: err?.name || "unknown",
        error_message: err?.message || "unknown",
        error_constraint: err?.constraint || undefined,
      });
    }

    // Track camera failure specifically in browser handoff sessions
    if (isBrowserHandoffSession && err?.type === "camera_error") {
      trackEvent("id_doc_browser_handoff_camera_failed", {
        error_name: err?.name || "unknown",
        error_message: err?.message || "unknown",
        error_constraint: err?.constraint || undefined,
      });
    }

    // Never reset to setup if a capture has already been initiated —
    // the toBlob callback may not have fired yet (it's async), so
    // phaseRef might still say "captureFront" even though we've captured.
    if (captureInitiatedRef.current) {
      console.log("[Flow] Ignoring error — capture already initiated");
      return;
    }
    // Only return to setup for fatal camera errors during FRONT capture.
    // Back capture errors are non-fatal — the camera was already working for the front,
    // so a back error is likely transient. The manual capture button will appear.
    if (err?.type === "camera_error" && currentPhase === "captureFront") {
      console.log("[Flow] Camera error during front capture → setup");
      setLoading(false);
      setPhaseSync("setup");
    } else {
      console.log("[Flow] Ignoring error (phase:", currentPhase, ")");
    }
  }

  function handleFaceCaptureComplete() {
    // Prefill selfie-only: no document was shot here, so the page is
    // still on the intro phase; move it on to the processing screen.
    if (prefill.selfieOnly) setPhaseSync("processing");
    setGotFace(true);
    setIsSDKComplete(true);
  }

  // ── Render guards ──
  // Log every render with current state so we can trace what's happening
  console.log("[Render] phase:", phase, "phaseRef:", phaseRef.current, "loading:", loading, "isSDKComplete:", isSDKComplete, "onMobile:", onMobile, "serverCheckPending:", serverCheckPending, "serverTamperDetected:", serverTamperDetected);

  // Prefill, every image supplied: no camera on this device, so this
  // comes ahead of the device check and the desktop handoff. A desktop
  // user is not sent to a phone to watch processing.
  if (prefill.complete) {
    return (
      <VerificationProcessingScreen
        type={settings?.journey?.settings?.isSelfie ? "document-selfie" : "document"}
        skipStages={["uploading"]}
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  if (showFirefoxPrompt) {
    console.log("[Render] → FirefoxCameraWarning");
    return <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />;
  }

  if (serverCheckPending) {
    console.log("[Render] → LoadingOverlay (serverCheckPending)");
    return <LoadingOverlay visible />;
  }

  if (serverTamperDetected) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {blockedLocale?.blocked?.title || "Unable to Continue"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {blockedLocale?.blocked?.message || "Please try again on your mobile device."}
        </Text>
      </Box>
    );
  }

  // Handoff for desktop — NEVER redirect during active capture/processing
  const showHandoffOnTablet = settings?.journey?.settings?.showHandoffOnTablet === true;
  const shouldShowQROnTablet = showHandoffOnTablet && isTablet;
  const shouldShowContinueOnDesktop = settings?.shouldShowContinueOnDesktop === true;

  if (!captureActive) {
    if (shouldShowQROnTablet && !onMobile) {
      console.log("[Render] → QR Handoff (tablet, not mobile)");
      return (
        <>
          <Handoff nextPath="id-doc/veratad-capture" />
          <Button onClick={() => setOnMobile(true)} mt="sm" fullWidth variant="subtle">
            {text?.qr?.troublePrimaryButton || "Continue on this device"}
          </Button>
        </>
      );
    }

    if (!onMobile && !shouldShowContinueOnDesktop) {
      console.log("[Render] → Handoff (not mobile, no continue-on-desktop). onMobile:", onMobile, "shouldShowContinueOnDesktop:", shouldShowContinueOnDesktop);
      return <Handoff nextPath="id-doc/veratad-capture" />;
    }
  }

  // ── Selfie capture ──
  if (getFace && !gotFace) {
    return <VeratadFaceCapture onCompletion={handleFaceCaptureComplete} />;
  }

  // ── Phase renders ──

  if (phase === "processing") {
    // Same three-bucket flow-type selection as dcams/index.js:
    // doc-only, doc-selfie pre-face (upload-only single stage),
    // doc-selfie post-face (verifying → face-to-doc → liveness →
    // completing). Polling is gated by shouldPoll so the
    // upload-only stage never fires onComplete.
    let flowType = "document";
    if (settings?.journey?.settings?.isSelfie) {
      flowType = needsSelfieNext ? "document-upload" : "document-selfie";
    }
    return (
      <VerificationProcessingScreen
        type={flowType}
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  // Keep DocumentCapture mounted across front→transition→back to preserve
  // the camera stream. The transition screen overlays on top.
  if (phase === "captureFront" || phase === "transition" || phase === "captureBack") {
    const captureSide = phase === "captureFront" ? "front" : "back";
    const isTransitioning = phase === "transition";
    console.log("[Render] → DocumentCapture (phase:", phase, "side:", captureSide, "paused:", isTransitioning, "loading:", loading, ")");

    if (isCaptureV2) {
      // Step indicator: front-only flows (passport picked, one-sided ID,
      // frontIdOnly sites) are a single step. YOLO passport detection
      // can still cut the flow short after the front — the header shows
      // 1 of 2 until then, which is the honest answer at that point.
      const backExpected = !(frontIdOnly || captureMode === "one_sided" || userDocType === "passport");
      return (
        <>
          {loading && <LoadingOverlay visible />}
          <DocumentCaptureV2
            side={captureSide}
            totalSteps={backExpected ? 2 : 1}
            captureSettings={captureSettings}
            onCapture={(blob, previewUrl, docType, conf, frameChain) =>
              handleCaptureForReview(captureSide, blob, previewUrl, docType, conf, frameChain)
            }
            onCaptureStarted={() => { captureInitiatedRef.current = true; }}
            onError={handleCaptureError}
            onReady={handleCaptureReady}
            onClose={handleCancelCapture}
            paused={isTransitioning}
            transitioning={isTransitioning}
            reviewImage={isStupidMode ? reviewData?.previewUrl || null : null}
            reviewSide={reviewData?.side || null}
            reviewImages={combinedReview ? { front: frontImage, back: backImage } : null}
            onReviewRetakeSide={handleCombinedRetake}
            onReviewAccept={combinedReview ? handleCombinedAccept : handleReviewAccept}
            onReviewRetake={handleReviewRetake}
            isBrowserHandoffSession={isBrowserHandoffSession}
            preAcquiredStream={preAcquiredStreamRef.current}
            requestId={settings.request_id}
            onPhotoFallback={handlePhotoFallback}
          />
        </>
      );
    }

    return (
      <>
        {loading && <LoadingOverlay visible />}
        <DocumentCapture
          side={captureSide}
          captureSettings={captureSettings}
          onCapture={(blob, previewUrl, docType, conf, frameChain) =>
            handleCaptureForReview(captureSide, blob, previewUrl, docType, conf, frameChain)
          }
          onCaptureStarted={() => { captureInitiatedRef.current = true; }}
          onError={handleCaptureError}
          onReady={handleCaptureReady}
          paused={isTransitioning}
          isBrowserHandoffSession={isBrowserHandoffSession}
          preAcquiredStream={preAcquiredStreamRef.current}
          requestId={settings.request_id}
          onPhotoFallback={handlePhotoFallback}
        />
        {isTransitioning && <FlipTransitionScreen locale={locale} />}

        {/* Review drawer — stupid mode: let user confirm or retake */}
        {isStupidMode && (
          <Drawer
            opened={!!reviewData}
            onClose={() => {}}
            position="bottom"
            size="100%"
            withCloseButton={false}
            zIndex={10001}
            transitionProps={{ duration: 900, timingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
            styles={{
              overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              },
              inner: {
                padding: 0,
              },
              content: {
                borderRadius: "20px 20px 0 0 !important",
                overflow: "hidden",
                backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : "#fff",
              },
              body: {
                padding: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {reviewData && <>
            {/* Image preview */}
            <Box
              style={{
                backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[8] : "#f5f5f5",
                padding: "20px 16px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                src={reviewData.previewUrl}
                alt="Captured document"
                style={{
                  width: "100%",
                  maxWidth: 380,
                  borderRadius: 10,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                }}
              />
            </Box>

            {/* Confirmation content */}
            <Box style={{ flex: 1, padding: "24px 20px 28px", display: "flex", flexDirection: "column" }}>
              {/* Title */}
              <Drawer.Title fw={700} fz={28} ta="center" mb="xl" lh={1.2}>
                {locale?.review?.title || "Does this look right?"}
              </Drawer.Title>

              {/* Checklist hints */}
              <Stack spacing="md" mb="xl" px={4}>
                {[
                  locale?.review?.hintVisible || "All four corners are visible",
                  locale?.review?.hintFocus || "Text is sharp and readable",
                  locale?.review?.hintGlare || "No glare, shadows, or fingers covering the ID",
                ].map((hint, i) => (
                  <Group key={i} spacing="md" noWrap>
                    <ThemeIcon
                      size={30}
                      radius="xl"
                      variant="light"
                      color={data?.styles?.primaryColor ?? theme.primaryColor}
                      // styles.components.docCapture.reviewCheckColor:
                      // checkmark + translucent disc independent of
                      // the primary palette. No-op when unset.
                      sx={
                        reviewCheckColor || reviewCheckBackground
                          ? {
                              ...(reviewCheckColor ? { color: reviewCheckColor } : {}),
                              backgroundColor:
                                reviewCheckBackground ||
                                tintOf(theme, reviewCheckColor, 0.15),
                            }
                          : undefined
                      }
                    >
                      <IconCheck size={18} stroke={2.5} />
                    </ThemeIcon>
                    <Text fz="md" fw={500}>{hint}</Text>
                  </Group>
                ))}
              </Stack>

              {/* Buttons — stacked, full width, pushed to bottom */}
              <Box style={{ marginTop: "auto" }}>
                <Stack spacing="sm">
                  <Button
                    size="lg"
                    fullWidth
                    onClick={handleReviewAccept}
                    leftIcon={<IconCheck size={20} />}
                  >
                    {locale?.review?.accept || "Looks Good"}
                  </Button>
                  <Button
                    variant="subtle"
                    size="md"
                    fullWidth
                    color="gray"
                    onClick={handleReviewRetake}
                    leftIcon={<IconRefresh size={18} />}
                  >
                    {locale?.review?.retake || "Retake Photo"}
                  </Button>
                </Stack>
              </Box>
            </Box>
            </>}
          </Drawer>
        )}
      </>
    );
  }

  // ── Setup screen ──
  console.log("[Render] → Setup screen (phase:", phase, ")");

  // Test harness — force the Android block portal on any device so testers
  // can review the screens without triggering a real camera error. Bypasses
  // every other gate. Matches the force_browser_handoff convention above.
  //   ?force_android_block=os_toggle         → Quick Settings variant
  //   ?force_android_block=chrome_permission → Chrome address-bar variant
  //   ?force_android_block=true              → defaults to os_toggle
  const forceAndroidBlock = new URLSearchParams(window.location.search).get("force_android_block");
  // Once the user has submitted a fallback file, we want the loading
  // overlay / processing phase to take over — so we stop blocking the
  // normal render flow with this early return.
  if (
    !fallbackInProgress &&
    (forceAndroidBlock === "os_toggle" ||
      forceAndroidBlock === "chrome_permission" ||
      forceAndroidBlock === "true")
  ) {
    return (
      <AndroidCameraBlockPortal
        locale={locale}
        requestId={settings.request_id}
        captureType="id_doc"
        isBrowserHandoffSession={isBrowserHandoffSession}
        androidVariant={
          forceAndroidBlock === "chrome_permission"
            ? "chrome_permission_legacy"
            : "os_toggle_12plus"
        }
        onPhotoFallback={handlePhotoFallback}
      />
    );
  }

  // Any camera error → camera-fallback portal. Fires for every device;
  // the portal's variant (generic / Android 12+ / Android 10-11) determines
  // whether Android-specific unblock instructions are shown below the
  // primary camera-app button. Supersedes the browser handoff flag for
  // camera errors — the camera-app fallback is a universally better
  // escape hatch than handing the user off to a different browser.
  if (cameraError && shouldShowAndroidCameraBlock()) {
    return (
      <AndroidCameraBlockPortal
        locale={locale}
        requestId={settings.request_id}
        captureType="id_doc"
        isBrowserHandoffSession={isBrowserHandoffSession}
        androidVariant={getAndroidBlockVariant() || "os_toggle_12plus"}
        onPhotoFallback={handlePhotoFallback}
      />
    );
  }

  // Non-Android: camera error + browser handoff flag on → show handoff screen.
  if (cameraError && shouldOfferBrowserHandoff) {
    return (
      <BrowserHandoff
        nextPath="id-doc/veratad-capture"
        onTryAgain={() => setCameraError(false)}
      />
    );
  }

  const instructions = [
    { Icon: IconSunHigh, text: locale?.setup?.wellLit || "You are in a well-lit area" },
    { Icon: IconId, text: locale?.setup?.flatSurface || "Place ID on a flat, dark surface" },
    { Icon: IconCapture, text: locale?.setup?.allCorners || "All four corners must be visible" },
  ];
  // v2 intro illustration (card + corner brackets) mirrors the capture
  // frame, so it follows styles.components.docCapture.glowColor too.
  const setupAccent =
    normalizeColor(data?.styles?.components?.docCapture?.glowColor) ||
    theme.colors[theme.primaryColor]?.[5] ||
    theme.colors.blue[5];
  const startDisabled = !isStupidMode && !offerOneSided && !modelReady;
  const onStart = offerOneSided
    ? () => setOneSidedDrawerOpen(true)
    : (isStupidMode ? () => setDocDrawerOpen(true) : handleStartCapture);
  const startLabel = startDisabled ? "Loading..." : (locale?.setup?.getStarted || "Get Started");

  // Pieces shared by the stacked (portrait) and side-by-side (landscape)
  // intro layouts, so the copy and the button are defined once.
  // The page title is the one <h1> on the screen (WCAG 1.3.1): screen
  // readers list it in the headings rota and VoiceOver's rotor jumps to it.
  const setupTitle = (
    <Text component="h1" m={0} fz={setupSideBySide ? 26 : 30} fw={700} align={setupSideBySide ? "left" : "center"} lh={1.2}>
      {locale?.setup?.title || "ID Document Verification"}
    </Text>
  );
  const setupLead = (
    <Text align={setupSideBySide ? "left" : "center"} c={setupSideBySide ? "dimmed" : undefined}>
      {locale?.setup?.instructions || "Before you get started ensure the following:"}
    </Text>
  );
  const setupCta = (
    <>
      {cameraError && (
        <Text fz="sm" c="red" fw={600} align="center" mb="sm">
          {locale?.errors?.cameraDenied ||
            "You have denied camera permissions. Please go into settings and allow camera, refresh the page, and try again."}
        </Text>
      )}
      {!setupSideBySide && (
        <Text fz="sm" c="dimmed" align="center" pb={15}>
          {locale?.setup?.cameraPermission || "We will ask you to allow camera permissions"}
        </Text>
      )}
      <Button size="lg" onClick={onStart} fullWidth disabled={startDisabled}>
        {startLabel}
      </Button>
      {setupSideBySide && (
        <Text fz="sm" c="dimmed" align="center" pt={8}>
          {locale?.setup?.cameraPermission || "We will ask you to allow camera permissions"}
        </Text>
      )}

      {/* Compact handoff for desktop with continue-on-desktop */}
      {!onMobile && shouldShowContinueOnDesktop && !qrScanned && (
        <CompactHandoff
          nextPath="id-doc/veratad-capture"
          onQrScanned={setQrScanned}
        />
      )}
    </>
  );

  return (
    <>
      {loading && <LoadingOverlay visible />}
      {setupSideBySide ? (
        // Landscape: capture-stage preview on the left, copy + CTA on the
        // right, both vertically centered. The stage is bound by the
        // available height so a phone held sideways still fits.
        // Both columns are centered with auto margins rather than
        // align-items: center, so when the viewport is short (Safari with
        // the tab bar open) the content grows the scroll box downward
        // instead of overflowing above the top where it can't be reached.
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: "clamp(20px, 4vw, 44px)",
            minHeight: "100%",
            padding: "0 0 8px",
            boxSizing: "border-box",
          }}
        >
          <Box style={{ flex: "0 1 44%", minWidth: 0, margin: "auto 0", display: "flex", justifyContent: "center" }}>
            <SetupStage
              accent={setupAccent}
              style={{ maxWidth: "min(420px, calc((100vh - 130px) * 1.3333))" }}
            />
          </Box>
          <Box style={{ flex: "1 1 56%", minWidth: 0, margin: "auto 0", display: "flex", flexDirection: "column", gap: 10 }}>
            {setupTitle}
            {setupLead}
            <Stack spacing={8} mb={4}>
              {instructions.map(({ Icon, text: instrText }, index) => (
                <Group key={index} noWrap spacing="sm">
                  <Icon size={24} stroke={1.6} color={setupIconColor || undefined} />
                  <Text fz={16}>{instrText}</Text>
                </Group>
              ))}
            </Stack>
            {setupCta}
          </Box>
        </Box>
      ) : (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box>
            <Stack align="center" spacing="md">
              <ThemeIcon
                variant="light"
                c={data?.styles?.primaryColor ?? theme.primaryColor}
                size={85}
                radius="xl"
                sx={
                  setupHeaderIconColor || setupHeaderIconBackground
                    ? {
                        ...(setupHeaderIconColor ? { color: setupHeaderIconColor } : {}),
                        ...(setupHeaderIconBackground || setupHeaderIconColor
                          ? {
                              backgroundColor:
                                setupHeaderIconBackground ||
                                tintOf(theme, setupHeaderIconColor, 0.15),
                            }
                          : {}),
                      }
                    : undefined
                }
              >
                <IconId size={70} stroke={1} />
              </ThemeIcon>
              {setupTitle}
              {setupLead}
              <Paper
                withBorder
                m="md"
                p="md"
                style={{
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[6]
                      : "#fafafa",
                  width: "100%",
                }}
              >
                <Stack spacing="xl">
                  {instructions.map(({ Icon, text: instrText }, index) => (
                    <Group key={index} noWrap>
                      <Icon size={30} color={setupIconColor || undefined} />
                      <Text fz={18}>{instrText}</Text>
                    </Group>
                  ))}
                </Stack>
              </Paper>
            </Stack>
          </Box>
          <Box>{setupCta}</Box>
        </Box>
      )}

      {/* Document type selection drawer (stupid mode) */}
      {isStupidMode && (
        <Drawer
          opened={docDrawerOpen}
          onClose={() => setDocDrawerOpen(false)}
          position="bottom"
          size="auto"
          withCloseButton={false}
          styles={{
            overlay: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
            inner: {
              padding: "0 8px 8px 8px",
            },
            content: {
              borderRadius: "12px !important",
              border: "1px solid #d0d0d0",
              boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            },
            body: {
              padding: "20px 20px 28px 20px",
            },
          }}
        >
          <Stack spacing="md">
            <Drawer.Title fw={600} fz="lg" ta="center" lh={1.3}>
              {locale?.setup?.docTypeQuestion || "What type of document do you have?"}
            </Drawer.Title>
            <Stack spacing="sm">
              <Paper
                onClick={() => handleDocTypeSelect("id_card")}
                p="md"
                withBorder
                sx={{
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  borderColor: docSelectBorder || theme.colors[theme.primaryColor]?.[4] || theme.colors.blue[4],
                  backgroundColor:
                    docSelectSelectedBg ||
                    (theme.colorScheme === "dark"
                      ? theme.colors[theme.primaryColor]?.[9] || theme.colors.blue[9]
                      : theme.colors[theme.primaryColor]?.[0] || theme.colors.blue[0]),
                  "&:hover": {
                    borderColor: docSelectBorder || theme.colors[theme.primaryColor]?.[5] || theme.colors.blue[5],
                    backgroundColor:
                      docSelectSelectedBg ||
                      (theme.colorScheme === "dark"
                        ? theme.colors.dark[5]
                        : theme.colors[theme.primaryColor]?.[1] || theme.colors.blue[1]),
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconCreditCard size={28} style={{ marginInlineEnd: 12, flexShrink: 0, color: docSelectIcon || theme.colors[theme.primaryColor]?.[6] || theme.colors.blue[6] }} />
                    <Text fw={500}>{locale?.setup?.idCard || "ID Card / License"}</Text>
                  </Box>
                  <IconChevronRight size={20} className="rtl-mirror" style={{ flexShrink: 0, color: theme.colors.gray[5] }} />
                </Box>
              </Paper>

              <Text size="sm" c="dimmed" ta="center" py={4}>
                {locale?.setup?.orLabel || "or"}
              </Text>

              <Paper
                onClick={() => handleDocTypeSelect("passport")}
                p="md"
                withBorder
                sx={{
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  ...(docSelectBorder ? { borderColor: docSelectBorder } : {}),
                  "&:hover": {
                    borderColor: docSelectBorder || theme.colors[theme.primaryColor]?.[5] || theme.colors.blue[5],
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[5]
                        : theme.colors.gray[0],
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconId size={28} style={{ marginInlineEnd: 12, flexShrink: 0, color: docSelectIcon || theme.colors[theme.primaryColor]?.[6] || theme.colors.blue[6] }} />
                    <Text fw={500}>{locale?.setup?.passport || "Passport"}</Text>
                  </Box>
                  <IconChevronRight size={20} className="rtl-mirror" style={{ flexShrink: 0, color: theme.colors.gray[5] }} />
                </Box>
              </Paper>
            </Stack>
          </Stack>
        </Drawer>
      )}

      {/* One-sided selection drawer (Japan MyNumber Card flow) */}
      {offerOneSided && (
        <Drawer
          opened={oneSidedDrawerOpen}
          onClose={() => setOneSidedDrawerOpen(false)}
          position="bottom"
          size="auto"
          withCloseButton={false}
          styles={{
            overlay: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
            inner: {
              padding: "0 8px 8px 8px",
            },
            content: {
              borderRadius: "12px !important",
              border: "1px solid #d0d0d0",
              boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            },
            body: {
              padding: "20px 20px 28px 20px",
            },
          }}
        >
          <Stack spacing="md">
            <Drawer.Title fw={600} fz="lg" ta="center" lh={1.3}>
              {oneSidedSectionTitle}
            </Drawer.Title>

            <Stack spacing="sm">
              {oneSidedDocumentOptions.map((option, index) => (
                <React.Fragment key={option.mode + index}>
                  {index > 0 && (
                    <Text size="sm" c="dimmed" ta="center" py={4}>
                      {orLabel}
                    </Text>
                  )}
                  <Paper
                    onClick={() => handleOneSidedSelection(option.mode)}
                    p="md"
                    withBorder
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      ...(docSelectBorder ? { borderColor: docSelectBorder } : {}),
                      "&:hover": {
                        borderColor: docSelectBorder || theme.colors[theme.primaryColor]?.[5] || theme.colors.blue[5],
                        backgroundColor:
                          theme.colorScheme === "dark"
                            ? theme.colors.dark[5]
                            : theme.colors.gray[0],
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <option.Icon size={28} style={{ marginInlineEnd: 12, flexShrink: 0, color: docSelectIcon || theme.colors[theme.primaryColor]?.[6] || theme.colors.blue[6] }} />
                        <Text fw={500}>{option.label}</Text>
                      </Box>
                      <IconChevronRight size={20} className="rtl-mirror" style={{ flexShrink: 0, color: theme.colors.gray[5] }} />
                    </Box>
                  </Paper>
                </React.Fragment>
              ))}
            </Stack>
          </Stack>
        </Drawer>
      )}
    </>
  );
}

// ── Build capture settings based on site config (mirrors Microblink settings) ──

// Which capture UI this session gets. URL override first (testing), then
// the Flagship flag from session settings; anything else is v1.
function resolveCaptureUi(settings) {
  try {
    const forced = new URLSearchParams(window.location.search).get("force_capture_v2");
    if (forced === "true") return "v2";
    if (forced === "false") return "v1";
  } catch {
    // no window / bad URL — fall through to the flag
  }
  const fromSettings = settings?.feature_flags?.[ID_DOC_CAPTURE_V2];
  if (typeof fromSettings === "boolean") return fromSettings ? "v2" : "v1";
  return flagEnabled(ID_DOC_CAPTURE_V2) ? "v2" : "v1";
}

function buildCaptureSettings(settings, captureUi = "v1") {
  const siteName = settings?.site_name || "";
  const siteNameLower = siteName.toLowerCase();
  const captureSettings = {};

  // Desktop or specific sites: disable blur + glare
  if (
    !isMobileDevice ||
    siteName === "GAVS-PROD-TEST" ||
    siteName === "GAVS-PROD-RO"
  ) {
    captureSettings.blurPolicy = "disabled";
    captureSettings.glarePolicy = "disabled";
  }

  // Veratad sites: manual capture only, no model loading. v1 = "stupid"
  // (no gates at all); v2 = "assisted" (light brightness / blur / glare
  // gates hold the shutter until the frame is usable).
  const isVeratadSite = siteNameLower.includes("veratad") || isKidSite(siteNameLower);
  if (isVeratadSite) {
    captureSettings.captureStrategy = captureUi === "v2" ? "assisted" : "stupid";
  }

  // v1 only: these tooDark=10 overrides predate the gates and never had
  // an effect in stupid mode (quality analysis is skipped). Assisted mode
  // starts from the preset's own floors; adjust them through the quality
  // overrides below instead.
  if (captureUi !== "v2") {
    if (isVeratadSite || ["kid-qa", "kid-pre-prod"].includes(siteNameLower)) {
      captureSettings.lightingThresholds = { tooDarkThreshold: 10 };
    }
  }

  // v2: layer the adjustable quality-gate thresholds on top of the
  // assisted preset and remember where each value came from.
  if (captureUi === "v2") {
    const { overrides, sources } = resolveQualityOverrides(settings);
    // Sharpness floor: the platform-specific key wins over the shared one.
    const os = captureOs();
    const blurKey = os === "android" && overrides.blurMinAndroid != null ? "blurMinAndroid"
      : os === "ios" && overrides.blurMinIos != null ? "blurMinIos"
      : overrides.blurMin != null ? "blurMin" : null;
    if (blurKey) {
      captureSettings.blurThreshold = overrides[blurKey];
      sources.blurMin = `${sources[blurKey]}:${blurKey}`;
    }
    if (overrides.glareMax != null) captureSettings.glareAreaThreshold = overrides.glareMax;
    if (overrides.glareMaxBack != null) captureSettings.glareAreaThresholdBack = overrides.glareMaxBack;
    if (overrides.lumaMin != null || overrides.lumaMax != null) {
      captureSettings.lightingThresholds = {
        ...(overrides.lumaMin != null ? { tooDarkThreshold: overrides.lumaMin } : {}),
        ...(overrides.lumaMax != null ? { tooBrightThreshold: overrides.lumaMax } : {}),
      };
    }
    if (overrides.lumaStdMin != null) captureSettings.lumaStdMin = overrides.lumaStdMin;
    if (overrides.darkRoom != null) captureSettings.darkRoomThreshold = overrides.darkRoom;
    if (overrides.motionMax != null) captureSettings.motionThreshold = overrides.motionMax;
    if (overrides.holdMs != null) captureSettings.qualityHoldMaxMs = overrides.holdMs;
    captureSettings.qualitySources = sources;
  }

  return captureSettings;
}
